import BN from "bignumber.js";
import ApiRequest from "@/common/lib/apiRequest";
import { hexlify, sleep } from "@/common/lib/utils";
import ERC20ABI from "@/common/config/abi/ERC20.json";

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "SET_AVAILABLE_BALANCE",
  SET_STAKE_BALANCES: "SET_STAKE_BALANCES",
  SET_ESTIMATED_APY: "SET_ESTIMATED_APY",
  SET_REWARDS: "SET_REWARDS",
  SET_ALLOWANCE: "SET_ALLOWANCE",
  RESET_STATE: "RESET_STATE",
  SET_PRICE_CHART_DATA: "SET_PRICE_CHART_DATA",
  SET_VALIDATOR_STAKE_INFO: "SET_VALIDATOR_STAKE_INFO",
  SET_BLOCK_NUMBER: "SET_BLOCK_NUMBER",
};

const createStakingState = () => ({
  availableBalance: {
    result: {
      balance: 0,
    },
    fetching: false,
  },
  rewards: {
    result: {
      cumulativeReward: 0,
      claimedReward: 0,
      claimableReward: 0,
      lockedReward: 0,
    },
    fetching: false,
  },
  stakeBalances: {
    result: {
      delegated: 0,
      unbonding: 0,
      withdrawable: 0,
    },
    fetching: false,
  },
  allowance: {
    fetching: false,
    result: null,
    error: null,
  },
});

const state = {
  estimatedApy: {
    result: null,
  },
  assetPriceChart: {
    fetching: false,
    result: [],
  },
  validatorStakeInfo: {
    result: null,
  },
  ethBlockNumber: null,
  ...createStakingState(),
};

const actions = {
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
  async fetchRequiredInfo({ dispatch, rootGetters }) {
    if (rootGetters["userWallets/walletSelected"].coin_network !== "ethereum") {
      return;
    }
    return Promise.all([
      dispatch("fetchAllowance"),
      dispatch("fetchAvailableBalance"),
      dispatch("fetchRewards"),
      dispatch("fetchStakeBalances"),
    ]);
  },
  async fetchAdditionalInfo({ dispatch }) {
    return Promise.all([
      dispatch("fetchEstimatedApy"),
      dispatch("fetchAssetPriceChart"),
      dispatch("fetchValidatorStakeInfo"),
    ]);
  },
  async fetchBlockNumber({ state, commit }) {
    return new Promise(async resolve => {
      if (!state.ethBlockNumber) {
        state.ethBlockNumber = -1;
        const { value } = await ApiRequest.get("/eth/celer/block_number");
        commit(mutationTypes.SET_BLOCK_NUMBER, value);
        resolve();
        setInterval(async () => {
          const { value } = await ApiRequest.get("/eth/celer/block_number");
          commit(mutationTypes.SET_BLOCK_NUMBER, value);
        }, 60000);
      } else {
        resolve();
      }
    });
  },
  async fetchValidatorStakeInfo({ commit }) {
    try {
      const result = await ApiRequest.get(`/eth/celer/total_staked`);
      commit(mutationTypes.SET_VALIDATOR_STAKE_INFO, {
        result,
      });
    } catch (error) {
      commit(mutationTypes.SET_VALIDATOR_STAKE_INFO, { error });
    }
  },
  async fetchEstimatedApy({ commit }) {
    try {
      const result = await ApiRequest.get(`/eth/celer/apy`);
      commit(mutationTypes.SET_ESTIMATED_APY, {
        result: {
          value: result.apy * 100,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_ESTIMATED_APY, { error });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_PRICE_CHART_DATA, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get("/eth/celer/celr_prices/" + startTime, {
        params: { type },
      });
      const result = getPriceChartData(data, type);
      commit(mutationTypes.SET_PRICE_CHART_DATA, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_PRICE_CHART_DATA, { error, fetching: false });
    }
  },
  async fetchAvailableBalance({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "/eth/celer/delegator/available_balance/" + address
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchStakeBalances({ state, commit, rootGetters, dispatch }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    commit(mutationTypes.SET_STAKE_BALANCES, { fetching: true });
    try {
      const [result] = await Promise.all([
        ApiRequest.get("/eth/celer/delegator/delegation/" + address),
        dispatch("fetchBlockNumber"),
      ]);
      commit(mutationTypes.SET_STAKE_BALANCES, {
        result: {
          delegated: result.delegatedStake,
          unbonding: result.undelegatingStake,
          withdrawable: result.withdrawableStake,
        },
        fetching: false,
      });
    } catch (error) {
      commit(mutationTypes.SET_STAKE_BALANCES, { error, fetching: false });
    }
  },
  async fetchRewards({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    commit(mutationTypes.SET_REWARDS, { fetching: true });
    try {
      const {
        cumulativeReward,
        claimedReward,
        claimableReward,
        lockedStakingReward: lockedReward,
      } = await ApiRequest.get("/eth/celer/delegator/reward/" + address);
      commit(mutationTypes.SET_REWARDS, {
        result: {
          cumulativeReward,
          claimedReward,
          claimableReward,
          lockedReward,
        },
        fetching: false,
      });
    } catch (error) {
      commit(mutationTypes.SET_REWARDS, { error, fetching: false });
    }
  },
  async fetchAllowance({ commit, rootGetters }, address) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    address = address || walletSelected?.address;
    if (!address) return;
    commit(mutationTypes.SET_ALLOWANCE, { fetching: true });
    try {
      const result = await ApiRequest.get(
        "eth/celer/delegator/allowance/" + address
      );
      if (address.toLowerCase() === walletSelected.address?.toLowerCase()) {
        commit(mutationTypes.SET_ALLOWANCE, { result, fetching: false });
      }
      return result;
    } catch (error) {
      commit(mutationTypes.SET_ALLOWANCE, { error, fetching: false });
    }
  },
  async getNonce({ rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(`/eth/celer/nonce/${address}`);
    return result.value;
  },
  async getApprovalRawTx({ rootGetters, dispatch }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/celer/delegator/digest/approve/${address}`),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getStakeRawTx({ rootGetters, dispatch }, { amount }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/celer/delegator/digest/delegate/${address}`, {
        params: { amount },
      }),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async initRedeemReward({ rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    await ApiRequest.post(`/eth/celer/delegator/init_redeem_reward/${address}`);
    await sleep(30000);
  },
  async getClaimRewardsRawTx({ rootGetters, dispatch }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/celer/delegator/digest/redeem_reward/${address}`),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getSendRawTx({ rootGetters, dispatch }, payload) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const web3 = rootGetters["appConfig/web3"]();
    const address = rootGetters["userWallets/walletSelected"].address;
    const getTokenByName = rootGetters["appConfig/getTokenByName"];
    const { contractAddress } = getTokenByName("celr");
    const contract = new web3.eth.Contract(ERC20ABI, contractAddress, {
      from: address,
    });
    const transferMethod = contract.methods.transfer(
      payload.toAddress,
      hexlify(payload.amount)
    );
    const [nonce, gasLimit, { fast }] = await Promise.all([
      dispatch("getNonce"),
      transferMethod.estimateGas(),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: transferMethod.encodeABI(),
    };
    return tx;
  },
  async getInitializeWithdrawRawTx({ rootGetters, dispatch }, { amount }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/celer/delegator/digest/intend_withdraw/${address}`, {
        params: { amount },
      }),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getConfirmWithdrawRawTx({ rootGetters, dispatch }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const [result, nonce] = await Promise.all([
      ApiRequest.get(`/eth/celer/delegator/digest/confirm_withdraw/${address}`),
      dispatch("getNonce"),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async broadcastTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    let url;
    let body = {
      type: payload.type,
      metadata: payload.metadata,
    };
    if (payload.signedTx) {
      body.signed_tx = payload.signedTx;
      url = `/eth/celer/transaction/broadcast/${walletSelected.address}`;
    } else {
      body.tx_hash = payload.txHash;
      url = `/eth/celer/transaction/create/${walletSelected.address}`;
    }
    return ApiRequest.post(url, body);
  },
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      ...data,
    };
  },
  [mutationTypes.SET_REWARDS](state, data) {
    state.rewards = {
      ...state.rewards,
      ...data,
    };
  },
  [mutationTypes.SET_STAKE_BALANCES](state, data) {
    state.stakeBalances = {
      ...state.stakeBalances,
      ...data,
    };
  },
  [mutationTypes.SET_ALLOWANCE](state, data) {
    state.allowance = {
      ...state.allowance,
      ...data,
    };
  },
  [mutationTypes.SET_ESTIMATED_APY](state, data) {
    state.estimatedApy = data;
  },
  [mutationTypes.SET_PRICE_CHART_DATA](state, data) {
    state.assetPriceChart = data;
  },
  [mutationTypes.SET_VALIDATOR_STAKE_INFO](state, data) {
    state.validatorStakeInfo = data;
  },
  [mutationTypes.SET_BLOCK_NUMBER](state, value) {
    state.ethBlockNumber = value;
  },
  [mutationTypes.RESET_STATE](state) {
    const stakingState = createStakingState();
    for (const key in stakingState) {
      state[key] = stakingState[key];
    }
  },
};

const getters = {
  availableBalance: state => state.availableBalance,
  rewards: state => state.rewards,
  stakeBalances: state => state.stakeBalances,
  totalAssets: state => {
    return new BN(state.availableBalance.result?.balance || 0)
      .plus(state.stakeBalances.result?.delegated || 0)
      .plus(state.stakeBalances.result?.withdrawable || 0)
      .plus(state.stakeBalances.result?.unbonding || 0)
      .plus(state.rewards.result?.lockedReward || 0)
      .plus(state.rewards.result?.claimableReward || 0)
      .toNumber();
  },
  needUnlockAssets: state => {
    return state.allowance.result?.value <= 0 ?? false;
  },
  estimatedApy: state => state.estimatedApy,
  assetPriceChart: state => state.assetPriceChart,
  validatorStakeInfo: state => state.validatorStakeInfo,
};

const getPriceChartData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  if (result.length === 1) return [result[0], result[0]];
  return result;
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
